import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
// eslint-disable-next-line no-restricted-imports
import Fuse, { FuseResult } from 'fuse.js';
import React from 'react';

import { FlexibleSelectTextField } from '@/components/atoms/FlexibleSelectTextField';
import { Recipient } from '@/components/molecules/Recipient';
import { SearchMenuItem } from '@/components/molecules/SearchMenuItem';
import { useTranslator } from '@/hooks/useTranslator';
import { RecipientAccount } from '@/types/contacts';

interface Props {
  readonly accounts: readonly RecipientAccount[];
  readonly value: string;

  readonly error?: boolean;
  readonly loading?: boolean;
  readonly name?: string;
  readonly helperText?: string | null;

  onChange(value: string): void;
}

export const RecipientsSelect: React.FC<Props> = (props: Props): React.ReactElement => {
  const { accounts, value, name, loading, onChange, error, helperText } = props;
  const [searchKeyword, setSearchKeyword] = React.useState<string>('');

  const tr = useTranslator();
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      onChange(value);
    },
    [onChange],
  );

  const fuse = React.useMemo((): Fuse<RecipientAccount> => {
    return new Fuse(accounts, {
      keys: ['name', 'description', 'currency'],
    });
  }, [accounts]);

  const filteredAccounts = React.useMemo((): readonly RecipientAccount[] => {
    if (searchKeyword.trim() !== '') {
      const results = fuse.search(searchKeyword);
      return results
        .map((result: FuseResult<RecipientAccount>): RecipientAccount => result.item)
        .sort(compareAccounts);
    }

    return [...accounts].sort(compareAccounts);
  }, [fuse, searchKeyword, accounts]);

  const onSearchKeywordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: keyword } = event.target;
      setSearchKeyword(keyword);
    },
    [],
  );

  const label = React.useMemo((): React.ReactNode => {
    if (loading) {
      return <Box sx={styles.loadingText} />;
    } else if (accounts.length === 0) {
      return tr('NO_RECIPIENT_ACCOUNTS');
    } else {
      return tr('SELECT_RECIPIENT_ACCOUNT_DETAILS');
    }
  }, [accounts.length, loading, tr]);

  const disabled = React.useMemo(
    (): boolean => accounts.length === 0 || !!loading,
    [accounts.length, loading],
  );

  return (
    <FlexibleSelectTextField
      name={name}
      label={label}
      select={true}
      value={value}
      fullWidth={true}
      error={error}
      helperText={helperText}
      disabled={disabled}
      onChange={handleChange}>
      <SearchMenuItem onChange={onSearchKeywordChange} />

      {filteredAccounts.map(
        (recipient: RecipientAccount): React.ReactElement => (
          <MenuItem key={recipient.id} value={recipient.id}>
            <Recipient account={recipient} />
          </MenuItem>
        ),
      )}
    </FlexibleSelectTextField>
  );
};

const styles: Record<string, SystemStyleObject> = {
  loadingText: {
    my: 0.75,
    height: 16,
    width: 240,
    animation: 'skeleton 1s infinite',
    backgroundColor: 'custom.Skeleton',
    borderRadius: 2,
  },
};

const compareAccounts = (a1: RecipientAccount, a2: RecipientAccount): number => {
  if (a1.id < a2.id) {
    return -1;
  } else if (a1.id > a2.id) {
    return 1;
  } else {
    return 0;
  }
};
